/**
 * Which banner the frame shows, kept free of React (Holatlar 5.2, 13 O1). Owner and moliyachi read
 * the credit count; everyone else is told what it means for them, without a figure.
 */
import type { ChannelAccount } from "@/api/messaging";
import { t } from "@/i18n";
import type { BannerTone } from "@/ui/banner";

export interface ShellAlert {
  readonly key: string;
  readonly tone: BannerTone;
  readonly text: string;
  readonly action?: { readonly label: string; readonly to: string } | undefined;
}

const DOWN: ReadonlySet<string> = new Set(["disconnected", "session_expired", "restricted"]);
const SEVERITY: Record<BannerTone, number> = { danger: 0, warning: 1, info: 2 };

export function creditAlert(input: {
  status: string | undefined;
  daysLeft: number | null | undefined;
  credits: number | null | undefined;
  canBuy: boolean;
}): ShellAlert | null {
  const action = input.canBuy ? { label: t("hol.credits.buy"), to: "/sozlamalar/tarif" } : undefined;
  if (input.status === "suspended_billing") {
    return { key: "credits", tone: "danger", text: t("hol.credits.out"), action };
  }
  const days = input.daysLeft;
  if (days === null || days === undefined || days > 7) return null;
  const tone: BannerTone = days <= 1 ? "danger" : "warning";
  const count = input.credits;
  if (count !== null && count !== undefined) {
    return { key: "credits", tone, text: t("hol.credits.full", { credits: count, days }), action };
  }
  return { key: "credits", tone, text: t(days <= 1 ? "hol.credits.short1" : "hol.credits.short3", { days }) };
}

export function channelAlerts(accounts: readonly ChannelAccount[]): ShellAlert[] {
  return accounts
    .filter((account) => DOWN.has(account.status))
    .map((account) => ({
      key: `channel-${String(account.id)}`,
      tone: "danger" as const,
      text: t("hol.channel.down", { label: account.label, status: account.status_display }),
      action: { label: t("hol.channel.reconnect"), to: "/sozlamalar/kanallar" },
    }));
}

/** The worst first: that one is shown and the rest fold behind "+N". */
export function ordered(alerts: readonly ShellAlert[]): ShellAlert[] {
  return [...alerts].sort((a, b) => SEVERITY[a.tone] - SEVERITY[b.tone]);
}
